import { useCallback, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, ScanLine } from 'lucide-react';
import { ApiError } from '@/api/client';
import { assetHealthApi, assetsApi, catalogApi, organizationApi } from '@/api/endpoints';
import { queryKeys } from '@/api/queryKeys';
import type { Asset, AssetCategory, CustomFieldDefinition } from '@/api/types';
import { usePermission } from '@/auth/AuthProvider';
import { PERMISSIONS } from '@/auth/permissions';
import {
  QuickAssetModelDialog,
  QuickAssetTypeDialog,
  QuickLocationDialog,
  QuickVendorDialog,
} from './QuickCatalogDialogs';
import { Button } from '@/design-system/Button';
import { InlineError } from '@/design-system/Feedback';
import { Select, TextArea, TextInput } from '@/design-system/Inputs';
import { Dialog } from '@/design-system/Overlay';
import { HealthPhotosField, todayIso } from './HealthChecksTab';
import { CustomFieldInputs } from './CustomFields';
import { LabelScanner } from './LabelScanner';
import { resolveRegistrationScan, type RegistrationScan } from './registrationScan';
import type { CustomFieldValue } from '@/api/types';

interface FormValues {
  assetTypeId: string;
  assetModelId: string;
  serialNumber: string;
  condition: string;
  locationId: string;
  vendorId: string;
  purchaseCost: string;
  salvageValue: string;
  currencyCode: string;
  purchasedOn: string;
  warrantyExpiresOn: string;
  notes: string;
}

/**
 * Registering an asset.
 *
 * Client-side validation exists to give fast, field-level feedback. It is not trusted: the server
 * validates everything again, and any rejection it returns is mapped back onto the offending field
 * so the user sees the problem where it is rather than as a banner.
 */
export function CreateAssetDialog({
  open,
  onClose,
  onCreated,
  category,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (asset: Asset) => void;
  /** Limits the type picker to one category - how accessories and general items are added. */
  category?: AssetCategory;
}) {
  const queryClient = useQueryClient();
  const [submitError, setSubmitError] = useState<unknown>(null);
  const [photos, setPhotos] = useState<File[]>([]);
  const [customValues, setCustomValues] = useState<Record<string, CustomFieldValue>>({});
  const [selectedSchema, setSelectedSchema] = useState<CustomFieldDefinition[]>([]);
  const [selectedSchemaSnapshot, setSelectedSchemaSnapshot] = useState('');

  // Adding to the catalogue or organisation from here is the same privilege as adding to it from Settings.
  const canEditCatalogue = usePermission(PERMISSIONS.CATALOG_WRITE);
  const canEditOrg = usePermission(PERMISSIONS.ORG_WRITE);
  const [addingType, setAddingType] = useState(false);
  const [addingModel, setAddingModel] = useState(false);
  const [addingLocation, setAddingLocation] = useState(false);
  const [addingVendor, setAddingVendor] = useState(false);
  const canTag = usePermission(PERMISSIONS.ASSET_TAG);
  const [scanning, setScanning] = useState(false);
  const [scan, setScan] = useState<RegistrationScan | { kind: 'checking' } | { kind: 'failed'; error: unknown } | null>(null);
  // Applied after the asset exists: registration itself has no tag field, and tagging is its own permission.
  const [pendingTag, setPendingTag] = useState<string | null>(null);
  // A lookup answering after the form was closed or reset must not prefill the next registration.
  const scanSeq = useRef(0);

  const {
    register,
    handleSubmit,
    watch,
    reset,
    setError,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    defaultValues: {
      assetTypeId: '',
      assetModelId: '',
      serialNumber: '',
      condition: 'NEW',
      locationId: '',
      vendorId: '',
      purchaseCost: '',
      salvageValue: '',
      currencyCode: '',
      purchasedOn: '',
      warrantyExpiresOn: '',
      notes: '',
    },
  });

  const assetTypeId = watch('assetTypeId');
  const locationId = watch('locationId');
  const vendorId = watch('vendorId');

  const assetTypes = useQuery({
    queryKey: queryKeys.catalog.assetTypes(),
    queryFn: catalogApi.assetTypes,
    enabled: open,
    staleTime: 10 * 60_000,
  });

  const typeOptions = (assetTypes.data ?? []).filter(
    (type) => !category || type.category === category,
  );
  const currentSelectedSchema = typeOptions.find(type => type.id === assetTypeId)?.customFieldSchema ?? [];
  const schemaChanged = Boolean(assetTypeId && JSON.stringify(currentSelectedSchema) !== selectedSchemaSnapshot);

  // Models are filtered by the chosen type, so the second choice can never contradict the first.
  const assetModels = useQuery({
    queryKey: queryKeys.catalog.assetModels(assetTypeId || undefined),
    queryFn: () => catalogApi.assetModels(assetTypeId || undefined),
    enabled: open && Boolean(assetTypeId),
  });

  const locations = useQuery({
    queryKey: queryKeys.organization.locations(),
    queryFn: organizationApi.locations,
    enabled: open,
    staleTime: 10 * 60_000,
  });

  const vendors = useQuery({
    queryKey: queryKeys.catalog.vendors(),
    queryFn: catalogApi.vendors,
    enabled: open,
    staleTime: 10 * 60_000,
  });

  const createAsset = useMutation({
    mutationFn: async (values: FormValues) => {
      const asset = await assetsApi.create({
        assetTypeId: values.assetTypeId,
        assetModelId: values.assetModelId,
        serialNumber: values.serialNumber || undefined,
        condition: values.condition as Asset['condition'],
        locationId: values.locationId || undefined,
        vendorId: values.vendorId || undefined,
        purchaseCost: values.purchaseCost ? Number(values.purchaseCost) : undefined,
        salvageValue: values.salvageValue === '' ? undefined : Number(values.salvageValue),
        currencyCode: values.currencyCode || undefined,
        purchasedOn: values.purchasedOn || undefined,
        warrantyExpiresOn: values.warrantyExpiresOn || undefined,
        notes: values.notes || undefined,
        customFields: customValues,
      });

      /*
       * Photos become the asset's first health check, dated today with the condition just chosen -
       * the record of what the device looked like when it entered the register.
       *
       * A second request, because a check has nowhere to attach until the asset exists. Its failure
       * does not fail the registration: the device is in the register, and losing that over an
       * upload would be far worse. The toast says so, and the photos can be added from the asset's
       * Health checks tab.
       */
      if (photos.length > 0 && asset.id) {
        try {
          await assetHealthApi.record(asset.id, {
            checkedOn: todayIso(),
            condition: values.condition,
            notes: 'Recorded when the asset was registered.',
            photos,
          });
        } catch {
          toast.warning('The photos could not be uploaded', {
            description:
              'The asset was registered. Add the photos again from its Health checks tab.',
          });
        }
      }
      if (pendingTag && asset.id) {
        try {
          return await assetsApi.applyTag(asset.id, pendingTag);
        } catch {
          toast.warning('The asset tag could not be applied', {
            description: 'The asset was registered. Apply the tag from its detail page.',
          });
        }
      }
      return asset;
    },
    onSuccess: (asset) => {
      // The new asset changes every list and count, so the whole namespace is invalidated.
      void queryClient.invalidateQueries({ queryKey: queryKeys.assets.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.reports.all });
      toast.success('Asset registered', {
        description: asset.assetTag ?? asset.serialNumber ?? 'The asset has been added.',
      });
      reset();
      setPhotos([]);
      setCustomValues({});
      setSelectedSchema([]); setSelectedSchemaSnapshot('');
      setScan(null); setPendingTag(null); scanSeq.current++;
      onCreated(asset);
    },
    onError: (error) => {
      // Field-level rejections are placed on the fields; anything else is shown as a summary.
      if (error instanceof ApiError && error.fieldErrors.length > 0) {
        error.fieldErrors.forEach((fieldError) => {
          setError(fieldError.field as keyof FormValues, { message: fieldError.message });
        });
        setSubmitError(null);
      } else {
        setSubmitError(error);
      }
    },
  });

  const close = () => {
    if (isSubmitting || createAsset.isPending) return;
    setSubmitError(null);
    reset();
    setPhotos([]);
    setCustomValues({});
    setSelectedSchema([]); setSelectedSchemaSnapshot('');
    setScan(null); setPendingTag(null); scanSeq.current++;
    onClose();
  };

  const applyScannedSerial = (value: string) => {
    setValue('serialNumber', value, { shouldValidate: true, shouldDirty: true });
    setScan(null);
  };
  const scanned = useCallback((raw: string) => {
    setScanning(false);
    setScan({ kind: 'checking' });
    const seq = ++scanSeq.current;
    resolveRegistrationScan(raw, window.location.origin).then(result => {
      if (seq !== scanSeq.current) return;
      if (result.kind === 'new' && !(result.tagShaped && canTag)) {
        setValue('serialNumber', result.value, { shouldValidate: true, shouldDirty: true });
        setScan(null);
      } else setScan(result);
    }, error => { if (seq === scanSeq.current) setScan({ kind: 'failed', error }); });
  }, [canTag, setValue]);

  return (
    <>
    <Dialog
      open={open}
      onClose={close}
      title={
        category === 'PERIPHERAL'
          ? 'Add an accessory'
          : category === 'OTHER'
            ? 'Add a general item'
            : 'Register an asset'
      }
      description="Adds a device to the register. Supplying a serial number registers it immediately; without one it stays as purchased until the detail is known."
      size="lg"
      busy={createAsset.isPending}
      footer={
        <>
          <Button variant="ghost" onClick={close} disabled={createAsset.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={createAsset.isPending}
            disabled={schemaChanged}
            onClick={handleSubmit((values) => { if (!schemaChanged) createAsset.mutate(values); })}
          >
            Register asset
          </Button>
        </>
      }
    >
      <form
        className="space-y-4"
        onSubmit={handleSubmit((values) => { if (!schemaChanged) createAsset.mutate(values); })}
        noValidate
      >
        {submitError ? <InlineError error={submitError} /> : null}

        <div className="space-y-2">
          <Button variant="secondary" onClick={() => setScanning(true)} disabled={createAsset.isPending || scan?.kind === 'checking'}>
            <ScanLine className="size-4" /> Scan label
          </Button>
          {scan?.kind === 'checking' && <p role="status" className="text-small">Checking the register for this label…</p>}
          {scan?.kind === 'failed' && <InlineError error={scan.error} />}
          {scan?.kind === 'invalid' && <p role="alert" className="text-small text-danger-text">{scan.message}</p>}
          {scan?.kind === 'existing' && (
            <div role="alert" className="text-small">
              This label is already registered as {scan.asset.assetTag ?? scan.asset.serialNumber ?? 'an asset'}.{' '}
              <Link to={`/assets/${scan.asset.id}`} onClick={close} className="underline">Open the existing asset</Link>
            </div>
          )}
          {scan?.kind === 'new' && (
            <div role="group" aria-label="Use scanned value" className="flex flex-wrap items-center gap-2 text-small">
              <span>{scan.value} is not in the register. Use it as:</span>
              <Button variant="secondary" onClick={() => applyScannedSerial(scan.value)}>Serial number</Button>
              <Button variant="secondary" onClick={() => { setPendingTag(scan.value); setScan(null); }}>Asset tag</Button>
            </div>
          )}
          {pendingTag && (
            <p className="text-small">
              Asset tag {pendingTag} will be applied after registering.{' '}
              <Button variant="ghost" onClick={() => setPendingTag(null)}>Remove tag</Button>
            </p>
          )}
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          {/*
           * Each picker carries its own "add" button rather than a link to Settings. A new
           * company's catalogue is empty, and the first person to register a device is holding
           * it - sending them to another screen to build reference data first is where this
           * flow used to stop.
           */}
          <div className="flex items-end gap-2">
            {/* The field, not the <select>, is the flex child: className lands on the control. */}
            <div className="min-w-0 flex-1">
            <Select
              label="Asset type"
              required
              placeholder={assetTypes.isLoading ? 'Loading…' : 'Choose a type'}
              error={errors.assetTypeId?.message}
              description={
                assetTypes.isSuccess && typeOptions.length === 0
                  ? canEditCatalogue
                    ? 'No asset types yet. Add the first one with the + button.'
                    : 'No asset types yet. Ask an administrator to add one under Settings → Asset catalogue.'
                  : undefined
              }
              options={typeOptions.map((type) => ({
                value: type.id ?? '',
                label: type.name ?? '',
              }))}
              {...register('assetTypeId', { required: 'Choose an asset type', onChange: event => {
                setCustomValues({});
                const selected = typeOptions.find(type => type.id === event.target.value)?.customFieldSchema ?? [];
                setSelectedSchema(selected.map(field => ({ ...field })));
                setSelectedSchemaSnapshot(JSON.stringify(selected));
              } })}
            />
            </div>
            {canEditCatalogue ? (
              <Button
                variant="secondary"
                aria-label="Add an asset type"
                title="Add an asset type"
                onClick={() => setAddingType(true)}
                // Matches the control beside it, so the row reads as one field.
                className="mb-px h-9 shrink-0 px-3"
              >
                <Plus className="size-4" />
              </Button>
            ) : null}
          </div>
          <div className="flex items-end gap-2">
            <div className="min-w-0 flex-1">
            <Select
              label="Model"
              required
              disabled={!assetTypeId}
              placeholder={
                !assetTypeId
                  ? 'Choose a type first'
                  : assetModels.isLoading
                    ? 'Loading…'
                    : 'Choose a model'
              }
              error={errors.assetModelId?.message}
              description={
                assetTypeId && assetModels.isSuccess && (assetModels.data ?? []).length === 0
                  ? canEditCatalogue
                    ? 'No models for this type yet. Add one with the + button.'
                    : 'No models for this type yet. Ask an administrator to add one.'
                  : undefined
              }
              options={(assetModels.data ?? []).map((model) => ({
                value: model.id ?? '',
                label: model.modelName ?? '',
              }))}
              {...register('assetModelId', { required: 'Choose a model' })}
            />
            </div>
            {canEditCatalogue ? (
              <Button
                variant="secondary"
                aria-label="Add a model"
                title={assetTypeId ? 'Add a model' : 'Choose an asset type first'}
                // A model belongs to a type, so there is nothing to add it to until one is chosen.
                disabled={!assetTypeId}
                onClick={() => setAddingModel(true)}
                className="mb-px h-9 shrink-0 px-3"
              >
                <Plus className="size-4" />
              </Button>
            ) : null}
          </div>
        </div>

        {schemaChanged && <p role="alert">The selected type's field definitions changed. Re-select the type and review the form before registering.</p>}
        <CustomFieldInputs schema={selectedSchema} values={customValues} onChange={setCustomValues} disabled={schemaChanged || createAsset.isPending} />

        <div className="grid gap-4 sm:grid-cols-2">
          <TextInput
            label="Serial number"
            placeholder="SN-000123"
            description="Registers the asset immediately when provided."
            error={errors.serialNumber?.message}
            {...register('serialNumber', {
              pattern: {
                value: /^[A-Za-z0-9._/-]*$/,
                message: 'Only letters, digits and . _ / - are allowed',
              },
              maxLength: { value: 120, message: 'At most 120 characters' },
            })}
          />
          <Select
            label="Condition"
            error={errors.condition?.message}
            options={['NEW', 'GOOD', 'FAIR', 'POOR', 'DAMAGED'].map((value) => ({
              value,
              label: value.charAt(0) + value.slice(1).toLowerCase(),
            }))}
            {...register('condition')}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="flex items-end gap-2">
            <div className="min-w-0 flex-1">
              <Select
                label="Location"
                placeholder="Not set"
                value={locationId}
                description={
                  locations.isSuccess && (locations.data ?? []).length === 0
                    ? canEditOrg
                      ? 'No locations yet. Add the first one with the + button.'
                      : 'No locations yet. Ask an administrator to add one under Settings → Organisation.'
                    : undefined
                }
                options={[
                  { value: '', label: 'Not set' },
                  ...(locations.data ?? []).map((location) => ({
                    value: location.id ?? '',
                    label: location.name ?? '',
                  })),
                ]}
                {...register('locationId')}
              />
            </div>
            {canEditOrg ? (
              <Button
                variant="secondary"
                aria-label="Add a location"
                title="Add a location"
                onClick={() => setAddingLocation(true)}
                className="mb-px h-9 shrink-0 px-3"
              >
                <Plus className="size-4" />
              </Button>
            ) : null}
          </div>

          <div className="flex items-end gap-2">
            <div className="min-w-0 flex-1">
              <Select
                label="Vendor"
                placeholder="Not set"
                value={vendorId}
                description={
                  vendors.isSuccess && (vendors.data ?? []).length === 0
                    ? canEditCatalogue
                      ? 'No vendors yet. Add the first one with the + button.'
                      : 'No vendors yet. Ask an administrator to add one under Settings → Asset catalogue.'
                    : undefined
                }
                options={[
                  { value: '', label: 'Not set' },
                  ...(vendors.data ?? []).map((vendor) => ({
                    value: vendor.id,
                    label: vendor.name,
                  })),
                ]}
                {...register('vendorId')}
              />
            </div>
            {canEditCatalogue ? (
              <Button
                variant="secondary"
                aria-label="Add a vendor"
                title="Add a vendor"
                onClick={() => setAddingVendor(true)}
                className="mb-px h-9 shrink-0 px-3"
              >
                <Plus className="size-4" />
              </Button>
            ) : null}
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-3">
          <TextInput
            label="Purchase cost"
            type="number"
            step="0.01"
            min="0"
            error={errors.purchaseCost?.message}
            {...register('purchaseCost', {
              min: { value: 0, message: 'Cost cannot be negative' },
            })}
          />
          <TextInput
            label="Currency"
            placeholder="INR"
            maxLength={3}
            description="ISO code"
            error={errors.currencyCode?.message}
            {...register('currencyCode', {
              pattern: { value: /^[A-Za-z]{0,3}$/, message: 'Three letters, e.g. INR' },
              // The server requires a currency whenever a cost is present.
              validate: (value, values) =>
                !values.purchaseCost || value
                  ? true
                  : 'A currency is required when a cost is given',
            })}
          />
          <TextInput label="Purchased on" type="date" {...register('purchasedOn')} />
        </div>

        <TextInput label="Salvage override" type="number" min="0" step="0.01" error={errors.salvageValue?.message}
          description="Optional amount in the purchase currency. Leave blank to use the asset type's salvage percentage; zero is a valid override."
          {...register('salvageValue', { validate: (value, values) => value === '' || (/^\d{1,13}(?:\.\d{1,2})?$/.test(value) && (values.purchaseCost === '' || Number(value) <= Number(values.purchaseCost))) || 'Use a nonnegative amount with at most two decimal places, no greater than purchase cost' })} />

        <TextInput
          label="Warranty expires"
          type="date"
          className="sm:max-w-56"
          {...register('warrantyExpiresOn')}
        />

        <TextArea
          label="Notes"
          rows={2}
          placeholder="Anything worth recording about this device"
          {...register('notes', { maxLength: { value: 4000, message: 'At most 4000 characters' } })}
        />

        <HealthPhotosField
          photos={photos}
          onChange={setPhotos}
          disabled={createAsset.isPending}
          description="Optional. Saved as the asset's first health check, dated today with the condition above. JPEG, PNG or WebP; large photos are resized."
        />
      </form>
    </Dialog>

    {/* Beside the dialog rather than inside its form: a form may not contain another form. */}
    <LabelScanner open={scanning} onClose={() => setScanning(false)} onScanned={scanned} />
    <QuickAssetTypeDialog
      open={addingType}
      category={category}
      onClose={() => setAddingType(false)}
      onCreated={(type) => {
        setAddingType(false);
        // Selected straight away: using it was the reason for adding it.
        setValue('assetTypeId', type.id ?? '', { shouldValidate: true });
        setCustomValues({});
        const schema = type.customFieldSchema ?? [];
        setSelectedSchema(schema.map((field) => ({ ...field })));
        setSelectedSchemaSnapshot(JSON.stringify(schema));
        // A brand-new type has no models, so whatever was chosen before cannot still apply.
        setValue('assetModelId', '');
      }}
    />
    <QuickAssetModelDialog
      open={addingModel}
      assetTypeId={assetTypeId}
      onClose={() => setAddingModel(false)}
      onCreated={(model) => {
        setAddingModel(false);
        setValue('assetModelId', model.id ?? '', { shouldValidate: true });
      }}
    />
    <QuickLocationDialog
      open={addingLocation}
      onClose={() => setAddingLocation(false)}
      onCreated={(location) => {
        setAddingLocation(false);
        setValue('locationId', location.id ?? '', { shouldValidate: true });
      }}
    />
    <QuickVendorDialog
      open={addingVendor}
      onClose={() => setAddingVendor(false)}
      onCreated={(vendor) => {
        setAddingVendor(false);
        setValue('vendorId', vendor.id ?? '', { shouldValidate: true });
      }}
    />
    </>
  );
}
